import React from 'react';
import {
  FiUsers, FiClipboard, FiSearch, FiCode, FiDatabase,
  FiCheckSquare, FiBookOpen, FiMonitor, FiHeadphones,
  FiArrowDownRight, FiArrowUpRight,
} from 'react-icons/fi';

const steps = [
  {
    title: 'Kickoff Meeting',
    icon: FiUsers,
    description: 'Define roles and responsibilities, confirm scope and timeline, align on the execution plan, and outline delivery milestones to onboard the project.',
  },
  {
    title: 'Requirement Gathering',
    icon: FiClipboard,
    description: 'Collect detailed business needs through structured team discussions and documentation.',
  },
  {
    title: 'Gap-Fit Analysis',
    icon: FiSearch,
    description: 'Identify gaps by comparing current (as-is) processes with future (to-be) goals, mapping key operations to Odoo modules, and evaluating what aligns out of the box, what needs configuration or customization, and where process adjustments are required.',
  },
  {
    title: 'Configuration & Development',
    icon: FiCode,
    description: 'Configure core modules and build custom features.',
  },
  {
    title: 'Data Migration',
    icon: FiDatabase,
    description: 'Extract, clean, and transform legacy data into Odoo.',
  },
  {
    title: 'Testing & Validation',
    icon: FiCheckSquare,
    description: 'Conduct functional, performance, and system testing to ensure software reliability.',
  },
  {
    title: 'Training & Change Management',
    icon: FiBookOpen,
    description: 'Equip users with role-based training and perform user acceptance training to confirm readiness.',
  },
  {
    title: 'Go-Live & Transition Support',
    icon: FiMonitor,
    description: 'Switch to the production environment and monitor hypercare.',
  },
  {
    title: 'Support & Maintenance',
    icon: FiHeadphones,
    description: 'Ensure a smooth shift from existing systems to Odoo through final data migration, user setup, and hypercare support to stabilize operations and quickly address post-launch issues.',
  },
];

function StepIcon({ icon: Icon, lower = false }) {
  return (
    <div className="relative flex h-20 w-20 shrink-0 items-center justify-center 2xl:h-28 2xl:w-28">
      <div
        className={`pointer-events-none absolute -inset-4 rounded-full border-[3px] border-dotted ${lower ? '[clip-path:inset(50%_0_0_0)]' : '[clip-path:inset(0_0_50%_0)]'}`}
        style={{ borderColor: '#969696' }}
        aria-hidden="true"
      />
      <div
        className="absolute inset-0 rounded-full border-[3px]"
        style={{ backgroundColor: '#FFFFFF', borderColor: '#949494' }}
        aria-hidden="true"
      />
      <div
        className={`absolute inset-[3px] rounded-full border-[7px] 2xl:border-[9px] ${lower ? '[clip-path:inset(50%_0_0_0)]' : '[clip-path:inset(0_0_50%_0)]'}`}
        style={{ borderColor: '#C3C3C3' }}
        aria-hidden="true"
      />
      <Icon className="relative h-8 w-8 2xl:h-11 2xl:w-11" strokeWidth={1.5} style={{ color: '#008BE8' }} aria-hidden="true" />
    </div>
  );
}

export default function OdooImplementationProcess() {
  return (
    <section
      className="overflow-hidden px-5 py-14 sm:px-8 sm:py-16 lg:px-10 xl:px-12"
      style={{ backgroundColor: '#FFFFFF', color: '#111111' }}
      aria-labelledby="odoo-process-heading"
    >
      <div className="mx-auto max-w-[1800px]">
        <h2
          id="odoo-process-heading"
          className="mb-10 text-center text-3xl font-bold leading-tight tracking-tight sm:mb-14 sm:text-4xl"
          style={{ color: '#111111' }}
        >
          Our Implementation Process
        </h2>

        {/* Alternating nine-stage timeline, following the reference layout. */}
        <ol className="mx-auto my-0 hidden w-[94%] list-none grid-cols-9 p-0 2xl:grid">
          {steps.map((step, index) => {
            const lower = index % 2 === 1;
            const Arrow = lower ? FiArrowUpRight : FiArrowDownRight;

            return (
              <li key={step.title} className="relative h-[860px]">
                <div className={`absolute left-1/2 w-[150%] -translate-x-1/2 text-center ${lower ? 'top-[616px] pt-10' : 'top-0 flex h-[368px] flex-col'}`}>
                  {lower && (
                    <div
                      className="absolute left-1/2 top-0 h-8 -translate-x-1/2 border-l-2 border-dotted"
                      style={{ borderColor: '#A3A3A3' }}
                      aria-hidden="true"
                    />
                  )}
                  <h3
                    className="m-0 flex min-h-[104px] shrink-0 items-end justify-center border-b-2 border-dotted px-2 pb-3 text-lg font-bold leading-tight 2xl:text-2xl"
                    style={{ borderColor: '#A3A3A3' }}
                  >
                    {step.title}
                  </h3>
                  <p
                    className="m-0 px-3 pt-4 text-sm font-medium leading-snug min-[1800px]:text-base"
                    style={{ color: '#303030', textAlign: 'center', hyphens: 'none', wordBreak: 'normal', overflowWrap: 'normal' }}
                  >
                    {step.description}
                  </p>
                  {!lower && (
                    <div
                      className="mx-auto mb-0 mt-auto h-8 shrink-0 border-l-2 border-dotted"
                      style={{ borderColor: '#A3A3A3' }}
                      aria-hidden="true"
                    />
                  )}
                </div>

                <div className={`absolute left-1/2 -translate-x-1/2 ${lower ? 'top-[488px]' : 'top-[384px]'}`}>
                  <StepIcon icon={step.icon} lower={lower} />
                </div>

                {index < steps.length - 1 && (
                  <Arrow
                    className={`absolute -right-4 h-8 w-8 2xl:-right-5 2xl:h-10 2xl:w-10 ${lower ? 'top-[480px]' : 'top-[476px]'}`}
                    strokeWidth={3}
                    style={{ color: '#969696' }}
                    aria-hidden="true"
                  />
                )}
              </li>
            );
          })}
        </ol>

        {/* Readable, sequential layout on phones and tablets. */}
        <ol className="mx-auto my-0 max-w-3xl list-none p-0 2xl:hidden">
          {steps.map((step, index) => (
            <li key={step.title} className="relative flex gap-7 pb-10 last:pb-0 sm:gap-10">
              {index < steps.length - 1 && (
                <div
                  className="absolute bottom-0 left-10 top-24 border-l-2 border-dotted"
                  style={{ borderColor: '#A3A3A3' }}
                  aria-hidden="true"
                />
              )}
              <StepIcon icon={step.icon} />
              <div className="min-w-0 pt-1">
                <h3 className="m-0 text-lg font-bold leading-snug sm:text-xl">{step.title}</h3>
                <p className="mb-0 mt-3 text-sm font-medium leading-relaxed sm:text-base" style={{ color: '#303030' }}>
                  {step.description}
                </p>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}
